import React, { Children, useMemo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import {
    HeaderWithActionButtons,
    HeaderWithActionButtonsProps,
} from './components/HeaderWithActionButtons';
import { templateWrapperStyles } from './landing-page-wrapper-with-left-tabs-helper';
import { TabsAndSelectFieldComponent } from 'components/landing-page-wrapper/components/TabsAndSelectFieldComponent';
import { TabItemsType } from 'components/landing-page-wrapper/components/TabComponent';
import { EmptyStateContainer } from 'components/empty-state-container/EmptyStateContainer';
import { Fab } from '@biq/apex-ui';
import AddIcon from '@mui/icons-material/Add';
import { paperWrapperStyles as wrapperStyles } from 'components/page-wrapper/page-wrapper-helper';

interface LandingPageWrapperWithLeftTabsProps
    extends HeaderWithActionButtonsProps {
    selectedTab?: string;
    onTabChange?: (event: React.SyntheticEvent, newValue: any) => void;
    tabItems?: TabItemsType[];
    tabsContainerStyles?: any;
    tabsStyles?: any;
    tabItemStyles?: any;
    isSelectFieldEnabled?: boolean;
}

export const LandingPageWrapperWithLeftTabs = ({
    readOnly,
    includeBreadcrumbs,
    breadCrumStatus,
    breadCrumType,
    breadCrumData,
    breadCrumLastItem,
    headingText,
    isLoading,
    isChangesUnsaved,
    unsavedChangesConfirmationText,
    openCancelModal,
    handleCancelButton,
    handleCancelModal,
    handleCancelModalConfirmation,
    handleSaveAndCloseButton,
    handleSaveButton,
    saveModalConfig,
    isMenuComponentVisible,
    menuWidth,
    menuItems,
    confirmationModalConfig,
    selectedTab,
    tabItems,
    onTabChange,
    tabsContainerStyles,
    tabsStyles,
    tabItemStyles,
    isSelectFieldEnabled = false,
}: LandingPageWrapperWithLeftTabsProps): React.JSX.Element => {
    const selectedTabItem: TabItemsType | undefined = useMemo(
        () => tabItems?.find((tab) => tab.label === selectedTab),
        [tabItems, selectedTab]
    );
    return (
        <>
            <Box sx={templateWrapperStyles.headerBox}>
                <HeaderWithActionButtons
                    readOnly={readOnly}
                    includeBreadcrumbs={includeBreadcrumbs}
                    breadCrumStatus={breadCrumStatus}
                    breadCrumType={breadCrumType}
                    breadCrumData={breadCrumData}
                    breadCrumLastItem={breadCrumLastItem}
                    headingText={headingText}
                    isLoading={isLoading}
                    isChangesUnsaved={isChangesUnsaved}
                    unsavedChangesConfirmationText={
                        unsavedChangesConfirmationText
                    }
                    openCancelModal={openCancelModal}
                    handleCancelButton={handleCancelButton}
                    handleCancelModal={handleCancelModal}
                    handleCancelModalConfirmation={
                        handleCancelModalConfirmation
                    }
                    handleSaveAndCloseButton={handleSaveAndCloseButton}
                    handleSaveButton={handleSaveButton}
                    saveModalConfig={saveModalConfig}
                    isMenuComponentVisible={isMenuComponentVisible}
                    menuWidth={menuWidth}
                    menuItems={menuItems}
                    confirmationModalConfig={confirmationModalConfig}
                />
            </Box>
            <Box sx={wrapperStyles.childContainer}>
                <Grid sx={templateWrapperStyles.bodyContainerStyles}>
                    {tabItems && tabItems.length > 0 && (
                        <TabsAndSelectFieldComponent
                            tabItems={tabItems}
                            selectedTab={selectedTab}
                            onTabChange={onTabChange ?? ((): void => {})}
                            tabsContainerStyles={tabsContainerStyles}
                            tabsStyles={tabsStyles}
                            tabItemStyles={tabItemStyles}
                            isSelectFieldEnabled={isSelectFieldEnabled}
                        />
                    )}

                    {selectedTabItem && (
                        <Box
                            key={selectedTabItem?.label}
                            sx={{
                                ...templateWrapperStyles.contentContainerStyles,
                                minHeight: selectedTabItem?.isEmptyState
                                    ? 'calc(100vh - 294px)'
                                    : '',
                                maxHeight: !selectedTabItem?.isEmptyState
                                    ? 'calc(100vh - 294px)'
                                    : '',
                            }}
                        >
                            {selectedTabItem?.tabHeaderConfig && (
                                <Grid
                                    display={'flex'}
                                    alignItems={'center'}
                                    justifyContent={'space-between'}
                                >
                                    <Grid>
                                        <Typography
                                            sx={
                                                templateWrapperStyles.headingTextStyles
                                            }
                                        >
                                            {
                                                selectedTabItem?.tabHeaderConfig
                                                    .headingText
                                            }
                                        </Typography>
                                    </Grid>
                                    {selectedTabItem?.tabHeaderConfig
                                        ?.headerButtonsConfig && (
                                        <Grid
                                            sx={
                                                templateWrapperStyles.headerButtonStyles
                                            }
                                        >
                                            {Children.toArray(
                                                selectedTabItem?.tabHeaderConfig?.headerButtonsConfig.map(
                                                    (item) => (
                                                        <Fab
                                                            type='button'
                                                            sx={item.sx}
                                                            icon={item.icon}
                                                            color='secondary'
                                                            label={item.label}
                                                            onClick={
                                                                item.onClick
                                                            }
                                                        />
                                                    )
                                                )
                                            )}
                                        </Grid>
                                    )}
                                </Grid>
                            )}

                            {selectedTabItem?.isEmptyState &&
                            selectedTabItem?.emptyStateConfig ? (
                                <Box
                                    sx={
                                        templateWrapperStyles.emptyContainerStyles
                                    }
                                >
                                    <Box>
                                        <EmptyStateContainer
                                            {...selectedTabItem?.emptyStateConfig}
                                        />
                                    </Box>
                                    {selectedTabItem?.emptyStateConfig
                                        .hasAddButton && (
                                        <Fab
                                            type='button'
                                            sx={
                                                templateWrapperStyles.addButtonStyles
                                            }
                                            icon={<AddIcon />}
                                            color='secondary'
                                            label={
                                                selectedTabItem
                                                    ?.emptyStateConfig
                                                    .addButtonLabel
                                            }
                                            onClick={
                                                selectedTabItem
                                                    ?.emptyStateConfig
                                                    .handleAddButtonClick
                                            }
                                        />
                                    )}
                                </Box>
                            ) : (
                                selectedTabItem?.tabContent ?? <></>
                            )}
                        </Box>
                    )}
                </Grid>
            </Box>
        </>
    );
};
